/** Speed of Service module — QSR speed KPIs, day-part heatmap, store drilldown. */
import React from 'react';
import { Timer, Gauge, CheckCircle2, AlertTriangle } from 'lucide-react';
import KpiTile from './KpiTile';
import Heatmap from './Heatmap';
import StoreCompareList from './StoreCompareList';
import CoryTips from '@/components/demo/CoryTips';
import { QSR_STORES, DAYPARTS, TARGETS, avgBy } from './qsrDemoData';

export default function SpeedOfServiceModule({ onSelectStore, onNavigate }) {
  const netSos = avgBy('sos');
  const pctUnder = Math.round((QSR_STORES.filter((s) => s.sos <= TARGETS.sos).length / QSR_STORES.length) * 100);
  const slowest = [...QSR_STORES].sort((a, b) => b.sos - a.sos)[0];
  const heat = QSR_STORES.map((s) => s.sosByDaypart.map((v) => +v.toFixed(1)));

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="speed-module">
      <CoryTips page="speed" variant="qsr" onAction={onNavigate} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Timer} label="Network speed of service" value={`${netSos.toFixed(1)}m`} sub={`target ${TARGETS.sos}m`} delta={`${(netSos - TARGETS.sos).toFixed(1)}m`} deltaGood={netSos <= TARGETS.sos} color="#3b82f6" trend={[4.1, 4.0, 3.9, 3.9, 3.8, 3.8, 3.7, 3.7, 3.6, 3.6, 3.5, netSos]} testid="speed-kpi-network" source="Lightspeed KDS · vs 3.5m QSR benchmark" ai={`Network speed of service is ${netSos.toFixed(1)}m vs a ${TARGETS.sos}m target. Southport's dinner window is the drag — one extra runner 5–8pm pulls the whole network under target.`} />
        <KpiTile icon={CheckCircle2} label="Stores under target" source="Lightspeed KDS · target 3.5m" value={`${pctUnder}%`} sub={`${QSR_STORES.length} locations`} color="#10b981" />
        <KpiTile icon={Gauge} label="Peak drive-through" source="Lightspeed KDS · dinner day-part" value="6.1m" sub="Southport · dinner" color="#f59e0b" />
        <KpiTile icon={AlertTriangle} label="Slowest store" source="Lightspeed KDS" value={slowest.name} sub={`${slowest.sos.toFixed(1)}m avg`} color="#ef4444" />
      </div>

      <Heatmap
        title="Speed of service by day-part (minutes · lower is better)"
        rows={QSR_STORES.map((s) => s.name)}
        cols={DAYPARTS}
        values={heat}
        unit="m"
        invert
        ai="Reds cluster on the lunch and dinner day-parts. Speed is a staffing problem, not a kitchen one — align rosters to these two peaks before adding equipment."
      />

      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2 px-1">Fastest stores — tap for the full profile</div>
        <StoreCompareList
          stores={QSR_STORES}
          sortKey="sos"
          sortDir="asc"
          trendKey="sosTrend"
          trendColor="#3b82f6"
          onSelect={onSelectStore}
          columns={[
            { label: 'Speed', fmt: (s) => `${s.sos.toFixed(1)}m` },
            { label: 'Accuracy', fmt: (s) => `${s.accuracy.toFixed(1)}%` },
          ]}
        />
      </div>
    </div>
  );
}
